/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import './sh-private-date-selector.js';
import './sh-private-month-selector.js';
import './sh-private-year-selector.js';

/**
 * A calendar-view component that switches the view between sh-private-date-selector,
 * sh-private-month-selector and sh-private-year-selector corresponding to the value given
 * to the currentView property.
 *
 * @prop {String} currentView - Describes the current view of the calendar-view-manager. Accepted values are `date`, `month`,`year`. Shows either `sh-private-date-selector`, `sh-private-month-selector` or `sh-private-year-selector` corresponding to these values.
 * @prop {Number} startingMonth - The starting month corresponding to the month property of the sh-private-date-selector when current-view is `date`.
 * @prop {Number} startingYear - The starting year corresponding to the year property of the sh-private-date-selector when current-view is `date` and the year property of the sh-private-month-selector when current-view is `month`.
 * @prop {Number} startingDecade - The starting decade corresponding to the decade property of the sh-private-year-selector when current-view is `year`.
 * @prop {Object} todaysDate - Today's Date of type Date.
 * @prop {Array} selectedDates - The array of selected dates if any. Maximum it can have 2 selected dates for the case of range property
 * and only one value if there is no range property.
 * @prop {Boolean} futureDateDisable - Whether future dates should be disabled or not.
 * @prop {Boolean} pastDateDisable - Whether past dates should be disabled or not.
 * @prop {Object} minDate - The minimum date below which all dates will be disabled. `minDate` value should be of type Date.
 * @prop {Object} maxDate - The maximum date above which all dates will be disabled. `maxDate` value should be of type Date.
 * @prop {Number} months - The number of views to be shown. Example months=2 shows 2 views of date selector starting from the given month and year.
 * The first view will show given month and year calendar. The second view shows next month and year calendar.
 * Any number of views can be generated by giving value to months property.
 * @prop {Boolean} range - Defines if the selector shows range selection or single selection.
 * @prop {Object} attachNode - Defines the datepicker element to attach to.
 * @prop {Boolean} responsive - Defines if the sub overlays should show responsive styles or not.
 * @prop {Boolean} close - setting this property sets the fade-out animation for this component.
 * @prop {String} locale - The locale value used for formatting the month and week days in the datepicker views
 */

export class SHPrivateCalendarView extends LitElement {
  static get properties() {
    return {
      currentView: { type: String },
      startingMonth: { type: Number },
      startingYear: { type: Number },
      startingDecade: { type: Number },
      todaysDate: { type: Object },
      selectedDates: { type: Array },
      futureDateDisable: { type: Boolean },
      pastDateDisable: { type: Boolean },
      minDate: { type: Object },
      maxDate: { type: Object },
      months: { type: Number },
      range: { type: Boolean },
      attachNode: { type: Object },
      responsive: { type: Boolean },
      locale: { type: String },
      close: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      css`
        :host {
          display: table;
          animation: fade-in var(--transition-time) ease-in-out;
        }
        @keyframes fade-in {
          0% {
            opacity: 0;
            visibility: hidden;
          }
          100% {
            opacity: 1;
            visibility: visible;
          }
        }
        :host([close]) {
          animation: fade-out var(--transition-time) ease-in-out;
        }
        @keyframes fade-out {
          0% {
            opacity: 1;
            visibility: visible;
          }
          100% {
            opacity: 0;
            visibility: hidden;
          }
        }
      `,
    ];
  }
  constructor() {
    super();
    this.selectedDates = [];
  }
  connectedCallback() {
    super.connectedCallback();
    this.documentBodyClickListener = this._documentBodyClickActions.bind(this);
    document.body.addEventListener('click', this.documentBodyClickListener);
  }
  render() {
    return html`${this.currentView === 'date'
      ? html`<sh-private-date-selector
          .months="${this.months}"
          .month="${this.startingMonth}"
          .year="${this.startingYear}"
          .todaysDate="${this.todaysDate}"
          .selectedDates="${this.selectedDates}"
          .futureDateDisable="${this.futureDateDisable}"
          .pastDateDisable="${this.pastDateDisable}"
          .minDate="${this.minDate}"
          .maxDate="${this.maxDate}"
          .range="${this.range}"
          @selected-dates-changed="${(e) => {
            e.stopPropagation();
            this.selectedDates = e.target.selectedDates;
          }}"
          @month-header-clicked="${(e) => {
            e.stopPropagation();
            this.startingYear = e.detail.clickedYear;
            this.currentView = 'month';
          }}"
          .responsive="${this.responsive}"
          .locale="${this.locale}"
        ></sh-private-date-selector>`
      : ''}
    ${this.currentView === 'month'
      ? html`<sh-private-month-selector
          .months="${this.months}"
          .year="${this.startingYear}"
          .todaysDate="${this._getMonthViewFormatDate(this.todaysDate)}"
          .minDate="${this._getMonthViewFormatDate(this.minDate)}"
          .maxDate="${this._getMonthViewFormatDate(this.maxDate)}"
          .selectedDate="${this._getMonthViewSelectedDate(this.selectedDates)}"
          .futureDateDisable="${this.futureDateDisable}"
          .pastDateDisable="${this.pastDateDisable}"
          .locale="${this.locale}"
          @month-clicked="${(e) => {
            e.stopPropagation();
            this.startingMonth = e.detail.clickedMonth;
            this.startingYear = e.detail.clickedYear;
            this.currentView = 'date';
          }}"
          @year-header-clicked="${(e) => {
            e.stopPropagation();
            this.startingDecade = e.detail.belongingDecade;
            this.currentView = 'year';
          }}"
          .responsive="${this.responsive}"
        ></sh-private-month-selector>`
      : ''}
    ${this.currentView === 'year'
      ? html`<sh-private-year-selector
          .months="${this.months}"
          .decade="${this.startingDecade}"
          .todaysDate="${this._getYearViewFormatDate(this.todaysDate)}"
          .selectedDate="${this._getYearViewSelectedDate(this.selectedDates)}"
          .minDate="${this._getYearViewFormatDate(this.minDate)}"
          .maxDate="${this._getYearViewFormatDate(this.maxDate)}"
          .futureDateDisable="${this.futureDateDisable}"
          .pastDateDisable="${this.pastDateDisable}"
          @year-clicked="${(e) => {
            e.stopPropagation();
            this.startingYear = e.detail.clickedYear;
            this.currentView = 'month';
          }}"
          .responsive="${this.responsive}"
        ></sh-private-year-selector>`
      : ''}`;
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('currentView') && this.currentView) {
      this.openCurrentView();
    }
  }
  disconnectedCallback() {
    document.body.removeEventListener('click', this.documentBodyClickListener);
    super.disconnectedCallback();
  }
  _documentBodyClickActions(e) {
    if (
      e.composedPath().indexOf(this) === -1 &&
      e.composedPath().indexOf(this.attachNode) === -1 &&
      !this.responsive
    ) {
      dispatchCustomEvent(this, 'calendar-closed');
    }
  }
  /**
   * Returns a copy of the input date
   * in the format required for month-view.
   *
   * @param {Date} date
   * @returns copy of date formatted for month-view
   */
  _getMonthViewFormatDate(date) {
    let dateCopy;
    if (date) {
      dateCopy = new Date(date.getTime());
      dateCopy.setDate(1);
      dateCopy.setHours(0, 0, 0, 0);
    }
    return dateCopy;
  }
  /**
   * Returns a copy of the input date
   * in the format required for year-view.
   *
   * @param {Date} date
   * @returns copy of date formatted for year-view
   */
  _getYearViewFormatDate(date) {
    let dateCopy;
    if (date) {
      dateCopy = new Date(date.getTime());
      dateCopy.setMonth(0, 1);
      dateCopy.setHours(0, 0, 0, 0);
    }
    return dateCopy;
  }
  _getMonthViewSelectedDate(selectedDates) {
    if (selectedDates.length === 2 && this.range) {
      return this._getMonthViewFormatDate(this.selectedDates[1]);
    } else if (selectedDates.length === 1 && !this.range) {
      return this._getMonthViewFormatDate(this.selectedDates[0]);
    } else {
      return null;
    }
  }
  _getYearViewSelectedDate(selectedDates) {
    if (selectedDates.length === 2 && this.range) {
      return this._getYearViewFormatDate(this.selectedDates[1]);
    } else if (selectedDates.length === 1 && !this.range) {
      return this._getYearViewFormatDate(this.selectedDates[0]);
    } else {
      return null;
    }
  }
  openCurrentView() {
    const selector =
      this.currentView === 'date'
        ? 'date-selector'
        : this.currentView === 'month'
        ? 'month-selector'
        : 'year-selector';
    const generatorFunction =
      this.currentView === 'date'
        ? 'generateCalendarDatas'
        : this.currentView === 'month'
        ? 'generateMonthViewData'
        : 'generateYearViewData';
    const selectorComponent = this.shadowRoot.querySelector(`sh-private-${selector}`);
    selectorComponent[generatorFunction]();
    selectorComponent.focus();
  }
}
customElements.define('sh-private-calendar-view', SHPrivateCalendarView);
